import React, { useCallback, useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Form, Button, Spin } from 'antd';
// Api
import { CREATE_CAMPAIGN_EXAMPLE_FILES } from 'api/campaignV2/mutations';
import { GET_CAMPAIGN } from 'api/campaignV2/queries';
// Helpers
import { getCustomBriefActivity } from 'helpers/cbActivity';
import {
  CustomCampaignDeliverable,
  CustomCampaignsEditModalProps,
  ExampleFiles,
  getCustomSearch,
  PendingExampleFile,
} from 'helpers/customCampaign';
import {
  buildCustomCampaignInitialValues,
  buildCustomCampaignSubmitValues,
  customCampaignValidationSchema,
} from 'helpers/customCampaignForm';
import { uploadImages } from 'helpers/campaignEditModal';
import { TimeLineDeliverable } from 'helpers/deliverables';
import { canEditPremiumFilters } from 'helpers/brands';
import { CustomCampaignStepOne } from 'helpers/customCampaignModeConfig';
// Types
import {
  GetCampaignsV2_getCampaigns_entities,
  GetCampaignsV2_getCampaigns_entities_search_CustomSearchCriteria_platformCriteria,
} from 'api/campaignV2/types/GetCampaignsV2';
import {
  GetCampaign,
  GetCampaignVariables,
} from 'api/campaignV2/types/GetCampaign';
import {
  CampaignStatus,
  CampaignTaskType,
  CampaignType,
} from 'api/graphql-global-types';
import {
  CreateCampaignExampleFiles,
  CreateCampaignExampleFilesVariables,
} from 'api/campaignV2/types/CreateCampaignExampleFiles';
// Hooks
import { useGetMyEntitlements } from 'hooks';
import { useCustomCampaignMode } from 'hooks/useCustomCampaignMode';
// Ui
import { errorNotification, successNotification } from 'ui/Notification';
// Components
import CustomCampaignTalentStep from './components/CustomCampaignTalentStep/CustomCampaignTalentStep';
import PreselectedTalentSection from './components/PreselectedTalentSection/PreselectedTalentSection';
import BriefSection from './components/BriefSection/BriefSection';
import PaymentSection from './components/PaymentSection/PaymentSection';
import TermsSection from './components/TermsSection/TermsSection';
// Utils
import {
  PlatformCriteriaItem,
  SOCIAL_DELIVERABLE_PLATFORMS,
} from 'helpers/customCampaignConstants';
// Style
import styles from './CustomCampaignsEditModal.module.scss';

const CustomCampaignsEditModalInner = ({
  record,
  onFinish,
}: CustomCampaignsEditModalProps): JSX.Element | null => {
  const { config } = useCustomCampaignMode(record.type);
  const [form] = Form.useForm();
  const [deliverablesValidationMessage, setDeliverablesValidationMessage] =
    useState<string | null>(null);
  const ownerId = record?.agency?.id;
  const { data: entitlementsData } = useGetMyEntitlements({
    ownerId,
    skip: !ownerId,
  });
  const canEditPremiumFiltersValue = canEditPremiumFilters(
    entitlementsData?.getMyEntitlements
  );

  const customBriefActivity = getCustomBriefActivity(record);
  const customSearch = getCustomSearch(record);

  // Once a direct booking leaves draft the agency has paid for the offer, so the
  // backend rejects amount/talent changes — lock those fields outside DETAIL_FILLED.
  const isDirectBookingOfferLocked =
    record.type === CampaignType.DirectBooking &&
    record.status !== CampaignStatus.DETAIL_FILLED;

  const [isPrivate, setIsPrivate] = useState<boolean>(
    record?.isPrivate ?? false
  );

  const [noUsageRights, setNoUsageRights] = useState<boolean>(
    !record?.usageRights
  );

  const [timelineDeliverablesChecklist, setTimelineDeliverablesChecklist] =
    useState<TimeLineDeliverable[]>(
      (customBriefActivity?.tasksV2 as unknown as TimeLineDeliverable[]) ?? []
    );

  // Preselect platforms from the saved CustomSearchCriteria.platformCriteria.
  // Only Instagram & TikTok are supported for custom campaigns for now.
  const [platformCriteria, setPlatformCriteria] = useState<
    PlatformCriteriaItem[]
  >(
    customSearch?.platformCriteria
      ?.filter((item) => SOCIAL_DELIVERABLE_PLATFORMS.includes(item.platform))
      .map(
        (
          item: GetCampaignsV2_getCampaigns_entities_search_CustomSearchCriteria_platformCriteria
        ) => ({
          platform: item.platform,
          minFollowers: item.minFollowers ?? null,
          maxFollowers: item.maxFollowers ?? null,
          minEngagementRate: item.minEngagementRate ?? null,
        })
      ) ?? []
  );

  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [selectedTalentId, setSelectedTalentId] = useState<string | null>(null);
  const [selectedTalentIds, setSelectedTalentIds] = useState<string[]>([]);

  const handleTalentIdsChange = useCallback((talentIds: string[]) => {
    setSelectedTalentIds((prev) => {
      if (
        prev.length === talentIds.length &&
        prev.every((id, index) => id === talentIds[index])
      ) {
        return prev;
      }
      return talentIds;
    });
  }, []);

  const handleVisibilityChange = useCallback((value: boolean) => {
    setIsPrivate(value);
  }, []);

  const initialFormValues = buildCustomCampaignInitialValues(
    record,
    customBriefActivity,
    timelineDeliverablesChecklist
  );

  const [updateCampaign, { loading: loadingUpdateCampaign }] = useMutation(
    config.mutation,
    {
      fetchPolicy: 'no-cache',
    }
  );

  const [
    createCampaignExampleFiles,
    { loading: loadingCreateCampaignExampleFiles },
  ] = useMutation<
    CreateCampaignExampleFiles,
    CreateCampaignExampleFilesVariables
  >(CREATE_CAMPAIGN_EXAMPLE_FILES);

  useEffect(() => {
    if (record) {
      form.setFieldsValue(initialFormValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?.id]);

  useEffect(() => {
    form.setFieldsValue({ deliverables: timelineDeliverablesChecklist });
    if (timelineDeliverablesChecklist.length) {
      setDeliverablesValidationMessage(null);
    }
  }, [form, timelineDeliverablesChecklist]);

  const handleUsageRightsChange = () => {
    setNoUsageRights((prev) => !prev);
  };

  const uploadPendingFiles = async (files: File[]): Promise<string[]> => {
    const { data: campaignExampleFiles } = await createCampaignExampleFiles({
      variables: {
        input: {
          files: files.map((file) => ({ name: file.name })),
        },
      },
    });

    if (!campaignExampleFiles?.createCampaignExampleFiles?.length) return [];

    setIsUploading(true);
    const urls = await uploadImages(
      campaignExampleFiles.createCampaignExampleFiles,
      files
    );
    setIsUploading(false);

    return urls;
  };

  const uploadDeliverableExampleFiles = async (
    deliverablesChecklist: TimeLineDeliverable[]
  ): Promise<CustomCampaignDeliverable[]> => {
    const filesToUpload: PendingExampleFile[] = [];
    const fileLocations: { taskIndex: number; exampleIndex: number }[] = [];

    deliverablesChecklist.forEach((deliverable, taskIndex) => {
      if ((deliverable as any).type === CampaignTaskType.APPEARANCE) return;

      (deliverable as CustomCampaignDeliverable).examples?.forEach(
        (example, exampleIndex) => {
          if (example instanceof File) {
            filesToUpload.push(example as PendingExampleFile);
            fileLocations.push({ taskIndex, exampleIndex });
          }
        }
      );
    });

    if (!filesToUpload.length) {
      return deliverablesChecklist as CustomCampaignDeliverable[];
    }

    const uploadedKeys = await uploadPendingFiles(filesToUpload);
    if (!uploadedKeys.length) {
      return deliverablesChecklist as CustomCampaignDeliverable[];
    }

    const updatedDeliverables = deliverablesChecklist.map((deliverable) =>
      Object.assign({}, deliverable, {
        examples: [
          ...(((deliverable as CustomCampaignDeliverable).examples ||
            []) as any[]),
        ],
      })
    ) as CustomCampaignDeliverable[];

    fileLocations.forEach(({ taskIndex, exampleIndex }, uploadIndex) => {
      const examples = updatedDeliverables[taskIndex].examples || [];
      examples[exampleIndex] = {
        key: uploadedKeys[uploadIndex],
        name: filesToUpload[uploadIndex].name,
      };
      updatedDeliverables[taskIndex].examples = examples;
    });

    setTimelineDeliverablesChecklist(updatedDeliverables);

    return updatedDeliverables;
  };

  const saveCampaign = async ({
    shouldValidate,
    talentIds,
  }: {
    shouldValidate: boolean;
    talentIds: string[];
  }): Promise<boolean> => {
    if (shouldValidate) {
      // Surface required-field validation errors instead of silently doing
      // nothing (antd's onFinish never fires when validation fails).
      try {
        await form.validateFields();
      } catch (validationError) {
        const firstMessage = (validationError as any)?.errorFields?.[0]
          ?.errors?.[0];
        errorNotification(firstMessage || 'Please fill in all required fields');
        return false;
      }
    }

    const deliverablesChecklist = timelineDeliverablesChecklist;
    form.setFieldsValue({ deliverables: deliverablesChecklist });
    const values = form.getFieldsValue(true);

    // Drafts can be saved on any step without filling in mandatory fields, so
    // only run the campaign validation schema when actually submitting.
    if (shouldValidate) {
      try {
        await customCampaignValidationSchema.validate({
          ...values,
          isPrivate,
          usageRights: noUsageRights,
          deliverables: deliverablesChecklist,
          platformCriteria,
        });
        setDeliverablesValidationMessage(null);
      } catch (validationError) {
        const message =
          (validationError as Error)?.message ||
          'Please fix the deliverables before submitting.';
        setDeliverablesValidationMessage(message);
        errorNotification(message);
        return false;
      }
    } else {
      setDeliverablesValidationMessage(null);
    }

    const exampleFiles = form.getFieldValue('contracts');
    const filesToUpload: File[] = [];
    const uploadUrls: string[] = [];

    exampleFiles?.forEach((file: ExampleFiles | File) => {
      if (!(file instanceof File) && file.key) {
        uploadUrls.push(file.key);
      } else if (file instanceof File) {
        filesToUpload.push(file);
      }
    });

    try {
      const uploadedDeliverablesChecklist = await uploadDeliverableExampleFiles(
        deliverablesChecklist
      );

      if (filesToUpload.length) {
        uploadUrls.push(...(await uploadPendingFiles(filesToUpload)));
      }

      const formValues = buildCustomCampaignSubmitValues({
        values,
        isPrivate,
        noUsageRights,
      });

      const input = config.buildUpdateInput({
        record,
        values: formValues,
        activity: customBriefActivity,
        deliverablesChecklist: uploadedDeliverablesChecklist,
        contracts: uploadUrls.map((key) => ({ key })),
        platformCriteria,
        talentId: selectedTalentId,
        talentIds,
      });

      const { data } = await updateCampaign({
        variables: { input },
      });

      return Boolean(data?.[config.mutationResultKey]);
    } catch (error) {
      setIsUploading(false);
      errorNotification((error as Error)?.message);
      console.error('Campaign update error:', { error });
      return false;
    }
  };

  const handleSubmit = async (shouldValidate = true) => {
    const didSave = await saveCampaign({
      shouldValidate,
      talentIds: selectedTalentIds,
    });

    if (didSave) {
      successNotification(config.successMessage);
      onFinish();
    }
  };

  // Adding/removing preselected talent persists immediately (the list is read
  // back from the campaign offers), so it must go through the same full-payload
  // save as Submit — a talentIds-only update would reset the rest of the brief.
  const handlePersistTalentIds = async (
    talentIds: string[]
  ): Promise<boolean> => {
    const didSave = await saveCampaign({ shouldValidate: false, talentIds });

    if (didSave) {
      handleTalentIdsChange(talentIds);
    }

    return didSave;
  };

  const submitLoading =
    isUploading || loadingUpdateCampaign || loadingCreateCampaignExampleFiles;
  if (!record) return null;

  return (
    <div className={styles.root}>
      <Form
        name="EditCustomCampaignForm"
        aria-label={config.title}
        layout="vertical"
        onFinish={() => handleSubmit(true)}
        form={form}
        initialValues={initialFormValues}
      >
        {record?.id &&
          config.stepOne === CustomCampaignStepOne.TalentAudience && (
            <CustomCampaignTalentStep
              campaignId={record.id}
              form={form}
              isPrivate={isPrivate}
              persistedIsPrivate={record?.isPrivate ?? false}
              onVisibilityChange={handleVisibilityChange}
              platformCriteria={platformCriteria}
              setPlatformCriteria={setPlatformCriteria}
              ownerId={ownerId}
              canEditPremiumFilters={canEditPremiumFiltersValue}
              onTalentIdsChange={handleTalentIdsChange}
              onPersistTalentIds={handlePersistTalentIds}
            />
          )}
        {record?.id &&
          config.stepOne === CustomCampaignStepOne.PreselectedTalent && (
            <PreselectedTalentSection
              campaignId={record.id}
              form={form}
              onTalentChange={setSelectedTalentId}
              locked={isDirectBookingOfferLocked}
            />
          )}
        <BriefSection
          deliverablesChecklist={timelineDeliverablesChecklist}
          setDeliverablesChecklist={setTimelineDeliverablesChecklist}
          availablePlatforms={platformCriteria.map((pc) => pc.platform)}
          deliverablesValidationMessage={deliverablesValidationMessage}
          noUsageRights={noUsageRights}
          onUsageRightsChange={handleUsageRightsChange}
          showApplicationWindow={config.briefFields.showApplicationWindow}
          showQuestions={config.briefFields.showQuestions}
        />

        <PaymentSection
          budget={record.reservedBudget ?? record.budget ?? null}
          showReservedBudget={config.payment.showReservedBudget}
          pricePerAthleteLabel={config.payment.pricePerAthleteLabel}
          pricePerAthleteLocked={isDirectBookingOfferLocked}
        />

        <TermsSection />

        <div className={styles.submitButtonWrapper}>
          <Button loading={submitLoading} onClick={() => handleSubmit(false)}>
            Save as Draft
          </Button>

          <Button
            type="primary"
            loading={submitLoading}
            onClick={() => handleSubmit(true)}
          >
            Submit Campaign
          </Button>
        </div>
      </Form>
    </div>
  );
};

// Wrapper that fetches the single campaign before mounting the editor. The
// list query (GetCampaignsV2) omits some fields (e.g.
// CustomSearchCriteria.platformCriteria), so we fetch the full campaign via
// getCampaign — mirroring millions-fe — and only render the editor once it has
// loaded, so all initial form state is derived from complete data.
const CustomCampaignsEditModal = ({
  record,
  onFinish,
  isOpen = true,
}: CustomCampaignsEditModalProps): JSX.Element | null => {
  const [fetchedCampaignId, setFetchedCampaignId] = useState<string | null>(
    null
  );
  const [getCampaign, { data, loading }] = useLazyQuery<
    GetCampaign,
    GetCampaignVariables
  >(GET_CAMPAIGN, { fetchPolicy: 'no-cache' });

  useEffect(() => {
    if (!isOpen || !record.id || fetchedCampaignId === record.id) return;

    setFetchedCampaignId(record.id);
    getCampaign({ variables: { input: { id: record.id } } });
  }, [fetchedCampaignId, getCampaign, isOpen, record.id]);

  const fetchedRecord = data?.getCampaign as
    | GetCampaignsV2_getCampaigns_entities
    | undefined;

  if (loading || !fetchedRecord) {
    return (
      <div className={styles.loaderWrapper}>
        <Spin />
      </div>
    );
  }

  return (
    <CustomCampaignsEditModalInner record={fetchedRecord} onFinish={onFinish} />
  );
};

export default CustomCampaignsEditModal;
